/**
 * useCalendarNavigation Hook
 * Manages view mode switching, date navigation, and focus management
 */
import { useState, useCallback, useRef } from 'react';
import { CalendarMode } from '../../../contracts/YearlyCalendarContracts';
import { navigateMonth, navigateYear, addMonths, subMonths, addYears, subYears } from '../utils/dateUtils';
import { announceToScreenReader } from '../utils/accessibilityUtils';
import { format } from 'date-fns';

export interface UseCalendarNavigationOptions {
  initialDate?: Date;
  initialViewMode?: CalendarMode;
  /**
   * Controlled view mode. When supplied the hook stops owning the mode and the
   * parent becomes the single source of truth - this is what lets the Resource
   * Dashboard page drive the Monthly/Yearly switcher without remounting the
   * calendar (and losing the active date).
   *
   * The parent MUST apply the mode reported by `onViewModeChange`, otherwise
   * internally-initiated switches (title toggle, month/year select) are lost.
   */
  viewMode?: CalendarMode;
  /**
   * Controlled active date. Same contract as `viewMode`: the parent MUST apply
   * the date reported by `onDateChange`. Needed by the monthly view, where the
   * page derives the two side mini-months from the active month and therefore
   * has to be able to move the main grid (e.g. when a side month is promoted).
   */
  activeDate?: Date;
  minDate?: Date;
  maxDate?: Date;
  onDateChange?: (date: Date) => void;
  onViewModeChange?: (mode: CalendarMode) => void;
}

export interface UseCalendarNavigationReturn {
  // State
  activeDate: Date;
  viewMode: CalendarMode;
  focusedDate: Date | null;
  
  // Navigation actions
  goToToday: () => void;
  goToPreviousMonth: () => void;
  goToNextMonth: () => void;
  goToPreviousYear: () => void;
  goToNextYear: () => void;
  goToDate: (date: Date) => void;
  
  // View mode actions
  setViewMode: (mode: CalendarMode) => void;
  toggleView: () => void;
  
  // Focus management
  setFocusedDate: (date: Date | null) => void;
  
  // Selection
  selectDate: (date: Date) => void;
  selectMonth: (month: number, year?: number) => void;
  selectYear: (year: number) => void;
  
  // Keyboard navigation
  handleKeyNavigation: (direction: 'up' | 'down' | 'left' | 'right') => void;
  
  // Refs for focus management
  calendarRef: React.RefObject<HTMLDivElement>;
}

/**
 * Custom hook for calendar navigation and view management
 */
export const useCalendarNavigation = (
  options: UseCalendarNavigationOptions = {}
): UseCalendarNavigationReturn => {
  const {
    initialDate = new Date(),
    initialViewMode = 'richcc-year',
    viewMode: controlledViewMode,
    activeDate: controlledActiveDate,
    minDate,
    maxDate,
    onDateChange,
    onViewModeChange,
  } = options;
  
  // State
  const [internalActiveDate, setInternalActiveDate] = useState<Date>(initialDate);
  const [internalViewMode, setInternalViewMode] = useState<CalendarMode>(initialViewMode);
  const [focusedDate, setFocusedDate] = useState<Date | null>(null);

  const viewMode = controlledViewMode ?? internalViewMode;
  const activeDate = controlledActiveDate ?? internalActiveDate;

  // Every caller below pairs setActiveDate with onDateChange, so a controlled
  // parent stays in sync while an uncontrolled one keeps the internal state.
  const setActiveDate = useCallback((date: Date) => {
    if (controlledActiveDate === undefined) {
      setInternalActiveDate(date);
    }
  }, [controlledActiveDate]);
  
  // Refs
  const calendarRef = useRef<HTMLDivElement>(null);
  
  // Helper to check date boundaries
  const isDateAllowed = useCallback((date: Date): boolean => {
    if (minDate && date < minDate) return false;
    if (maxDate && date > maxDate) return false;
    return true;
  }, [minDate, maxDate]);
  
  // Navigate to today
  const goToToday = useCallback(() => {
    const today = new Date();
    if (isDateAllowed(today)) {
      setActiveDate(today);
      setFocusedDate(today);
      onDateChange?.(today);
      announceToScreenReader(`Navigated to today, ${format(today, 'MMMM d, yyyy')}`);
    }
  }, [isDateAllowed, onDateChange]);
  
  // Navigate to previous month
  const goToPreviousMonth = useCallback(() => {
    const newDate = subMonths(activeDate, 1);
    if (isDateAllowed(newDate)) {
      setActiveDate(newDate);
      onDateChange?.(newDate);
      announceToScreenReader(`${format(newDate, 'MMMM yyyy')}`);
    }
  }, [activeDate, isDateAllowed, onDateChange]);
  
  // Navigate to next month
  const goToNextMonth = useCallback(() => {
    const newDate = addMonths(activeDate, 1);
    if (isDateAllowed(newDate)) {
      setActiveDate(newDate);
      onDateChange?.(newDate);
      announceToScreenReader(`${format(newDate, 'MMMM yyyy')}`);
    }
  }, [activeDate, isDateAllowed, onDateChange]);
  
  // Navigate to previous year
  const goToPreviousYear = useCallback(() => {
    const newDate = subYears(activeDate, 1);
    if (isDateAllowed(newDate)) {
      setActiveDate(newDate);
      onDateChange?.(newDate);
      announceToScreenReader(`${format(newDate, 'yyyy')}`);
    }
  }, [activeDate, isDateAllowed, onDateChange]);
  
  // Navigate to next year
  const goToNextYear = useCallback(() => {
    const newDate = addYears(activeDate, 1);
    if (isDateAllowed(newDate)) {
      setActiveDate(newDate);
      onDateChange?.(newDate);
      announceToScreenReader(`${format(newDate, 'yyyy')}`);
    }
  }, [activeDate, isDateAllowed, onDateChange]);
  
  // Navigate to specific date
  const goToDate = useCallback((date: Date) => {
    if (isDateAllowed(date)) {
      setActiveDate(date);
      setFocusedDate(date);
      onDateChange?.(date);
    }
  }, [isDateAllowed, onDateChange]);
  
  // Set view mode
  const setViewMode = useCallback((mode: CalendarMode) => {
    // In controlled mode the parent applies the change via the callback below;
    // writing internal state as well would fork the two sources of truth.
    if (controlledViewMode === undefined) {
      setInternalViewMode(mode);
    }
    onViewModeChange?.(mode);
    announceToScreenReader(`View changed to ${mode} mode`);
  }, [controlledViewMode, onViewModeChange]);
  
  // Toggle between views
  const toggleView = useCallback(() => {
    const modeOrder: CalendarMode[] = ['day', 'month', 'year', 'richcc-year'];
    const currentIndex = modeOrder.indexOf(viewMode);
    const nextIndex = (currentIndex + 1) % modeOrder.length;
    setViewMode(modeOrder[nextIndex]);
  }, [viewMode, setViewMode]);
  
  // Select a date (from day view)
  const selectDate = useCallback((date: Date) => {
    if (isDateAllowed(date)) {
      setActiveDate(date);
      setFocusedDate(date);
      onDateChange?.(date);
    }
  }, [isDateAllowed, onDateChange]);
  
  // Select a month (from month picker)
  const selectMonth = useCallback((month: number, year?: number) => {
    const newDate = new Date(year ?? activeDate.getFullYear(), month, 1);
    setActiveDate(newDate);
    setViewMode('day');
    onDateChange?.(newDate);
    announceToScreenReader(`Selected ${format(newDate, 'MMMM yyyy')}`);
  }, [activeDate, setViewMode, onDateChange]);
  
  // Select a year (from year picker)
  const selectYear = useCallback((year: number) => {
    const newDate = new Date(year, activeDate.getMonth(), 1);
    setActiveDate(newDate);
    setViewMode('month');
    onDateChange?.(newDate);
    announceToScreenReader(`Selected ${year}`);
  }, [activeDate, setViewMode, onDateChange]);
  
  // Handle keyboard navigation
  const handleKeyNavigation = useCallback((direction: 'up' | 'down' | 'left' | 'right') => {
    if (!focusedDate) {
      setFocusedDate(activeDate);
      return;
    }
    
    let newDate: Date | null = null;
    
    switch (viewMode) {
      case 'day':
      case 'richcc-year':
        // Navigate by days
        switch (direction) {
          case 'left':
            newDate = new Date(focusedDate);
            newDate.setDate(newDate.getDate() - 1);
            break;
          case 'right':
            newDate = new Date(focusedDate);
            newDate.setDate(newDate.getDate() + 1);
            break;
          case 'up':
            newDate = new Date(focusedDate);
            newDate.setDate(newDate.getDate() - 7);
            break;
          case 'down':
            newDate = new Date(focusedDate);
            newDate.setDate(newDate.getDate() + 7);
            break;
        }
        break;
        
      case 'month':
        // Navigate by months
        switch (direction) {
          case 'left':
            newDate = subMonths(focusedDate, 1);
            break;
          case 'right':
            newDate = addMonths(focusedDate, 1);
            break;
          case 'up':
            newDate = subMonths(focusedDate, 3);
            break;
          case 'down':
            newDate = addMonths(focusedDate, 3);
            break;
        }
        break;
        
      case 'year':
        // Navigate by years
        switch (direction) {
          case 'left':
            newDate = subYears(focusedDate, 1);
            break;
          case 'right':
            newDate = addYears(focusedDate, 1);
            break;
          case 'up':
            newDate = subYears(focusedDate, 5);
            break;
          case 'down':
            newDate = addYears(focusedDate, 5);
            break;
        }
        break;
    }
    
    if (newDate && isDateAllowed(newDate)) {
      setFocusedDate(newDate);
      announceToScreenReader(format(newDate, 'EEEE, MMMM d, yyyy'));
    }
  }, [focusedDate, activeDate, viewMode, isDateAllowed]);
  
  return {
    activeDate,
    viewMode,
    focusedDate,
    goToToday,
    goToPreviousMonth,
    goToNextMonth,
    goToPreviousYear,
    goToNextYear,
    goToDate,
    setViewMode,
    toggleView,
    setFocusedDate,
    selectDate,
    selectMonth,
    selectYear,
    handleKeyNavigation,
    calendarRef,
  };
};

export default useCalendarNavigation;
